import React, { useState } from "react";
import { Box, Dialog, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { resizedImage } from "../../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, VISUALLY_HIDDEN } from "../../../utils/designTokens";
import { DetailCard, outlineButtonSx } from "./detailUi";
import { formatPhone, telHref } from "./orderProgress";

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "?";

const TRANSPORT = { car: "Car", bike: "Bike", bicycle: "Bicycle", scooter: "Scooter", walk: "On foot" };

function Photo({ src, caption, onOpen }) {
  return (
    <Box component="figure" sx={{ m: 0, display: "flex", flexDirection: "column", gap: 0.75, flexShrink: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        sx={{
          ...BUTTON_RESET,
          ...FOCUS_RING,
          position: "relative",
          width: 120,
          height: 80,
          borderRadius: "14px",
          overflow: "hidden",
          bgcolor: C.surface2,
          cursor: "zoom-in",
          "&:hover .od-zoom, &:focus-visible .od-zoom": { opacity: 1 },
        }}
      >
        <Box component="img" src={resizedImage(src, 240)} alt={caption} loading="lazy" sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
        <Box className="od-zoom" aria-hidden="true" sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity .15s ease" }}>
          <ZoomInRoundedIcon sx={{ color: "#FFFFFF", fontSize: 26 }} />
        </Box>
        <Box component="span" sx={VISUALLY_HIDDEN}>Enlarge</Box>
      </Box>
      <Box component="figcaption" sx={{ fontFamily: FONT_BODY, fontSize: 12, color: C.muted }}>
        {caption}
      </Box>
    </Box>
  );
}

/**
 * The driver, once one is assigned: our rider (call, chat while the order is
 * live) or First Delivery's courier, their tracking link, and the photos the
 * driver took (drop-off, pickup receipt). `onTrackDriver` scrolls to the live
 * map; `trackDriverHref` is the courier's own tracking page.
 */
function DriverCard({ name, phone, photo, caption, transport, live, onChat, unreadChat = 0, trackDriverHref, onTrackDriver, photos = [] }) {
  const [enlarged, setEnlarged] = useState(null);
  const tel = telHref(phone);

  return (
    <DetailCard aria-label="Your driver" sx={{ p: { xs: "20px", sm: "20px 24px" } }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flex: "1 1 240px", minWidth: 0 }}>
          <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: "50%", overflow: "hidden", bgcolor: C.surface2, color: C.ink, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_BODY, fontSize: 18, fontWeight: 700 }}>
            {photo ? <Box component="img" src={resizedImage(photo, 128)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(name)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.muted }}>
              {caption}
              {transport && TRANSPORT[transport] ? ` · ${TRANSPORT[transport]}` : ""}
            </Box>
            <Box sx={{ fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Box>
            {tel && (
              <Box component="a" href={tel} sx={{ ...FOCUS_RING, display: "inline-block", mt: 0.25, borderRadius: "6px", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.redLink, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                {formatPhone(phone)}
                <Box component="span" sx={VISUALLY_HIDDEN}>{`, call ${name}`}</Box>
              </Box>
            )}
          </Box>
        </Box>

        {photos.map((p) => (
          <Photo key={p.src} src={p.src} caption={p.caption} onOpen={() => setEnlarged(p)} />
        ))}

        {(onChat || trackDriverHref || onTrackDriver) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: { sm: "auto" } }}>
            {live && onChat && (
              <Box component="button" type="button" onClick={onChat} sx={{ ...outlineButtonSx(false), position: "relative" }}>
                <ChatBubbleOutlineRoundedIcon aria-hidden="true" sx={{ fontSize: 19 }} />
                Chat
                {unreadChat > 0 && (
                  <Box component="span" sx={{ minWidth: 20, height: 20, px: 0.5, boxSizing: "border-box", borderRadius: "999px", bgcolor: C.red, color: "#FFFFFF", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {unreadChat > 9 ? "9+" : unreadChat}
                    <Box component="span" sx={VISUALLY_HIDDEN}> unread</Box>
                  </Box>
                )}
              </Box>
            )}
            {trackDriverHref ? (
              <Box component="a" href={trackDriverHref} target="_blank" rel="noopener noreferrer" sx={outlineButtonSx(true)}>
                Track driver <span aria-hidden="true">→</span>
                <Box component="span" sx={VISUALLY_HIDDEN}> (opens in a new tab)</Box>
              </Box>
            ) : onTrackDriver ? (
              <Box component="button" type="button" onClick={onTrackDriver} sx={outlineButtonSx(true)}>
                Track driver <span aria-hidden="true">→</span>
              </Box>
            ) : null}
          </Box>
        )}
      </Box>

      <Dialog
        open={Boolean(enlarged)}
        onClose={() => setEnlarged(null)}
        maxWidth={false}
        aria-label={enlarged?.caption || "Photo"}
        slotProps={{ backdrop: { sx: { bgcolor: "rgba(0,0,0,0.85)" } } }}
        PaperProps={{ sx: { m: 2, bgcolor: "transparent", boxShadow: "none", overflow: "visible", position: "relative", borderRadius: 0 } }}
      >
        {enlarged && (
          <Box component="img" src={enlarged.src} alt={enlarged.caption} sx={{ display: "block", maxWidth: "calc(100vw - 32px)", maxHeight: "calc(100vh - 32px)", objectFit: "contain", borderRadius: "14px" }} />
        )}
        <IconButton onClick={() => setEnlarged(null)} aria-label="Close photo" sx={{ position: "absolute", top: 8, right: 8, color: "#FFFFFF", bgcolor: "rgba(0,0,0,0.55)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}>
          <CloseRoundedIcon />
        </IconButton>
      </Dialog>
    </DetailCard>
  );
}

export default DriverCard;
